'use client'

import React, { useEffect, useState } from 'react'
import styles from './styles.module.css'
import { useCartStore, selectCart } from '../../../../store/cartStore'
import { useUiStore } from '../../../../store/uiStore'
// Offers migrated off Overmind (Phase D1). This panel lives in the global chrome
// OUTSIDE the page-content <RegionOffersProvider>, so it reads the offers from the
// GLOBAL offers store (the reason offers are Zustand, not a provider): useRegionOffers
// resolves to the store's FULL offers once fetched (else the transitional Overmind
// grid), and ensureFullOffers loads them lazily when the cart opens.
import { useRegionOffers } from '../../../RegionOffers'
import { useRegionCountryCode } from '../../../RegionGeo'
import { useOffersStore } from '../../../../store/offersStore'
import Text from '../../../Text'
import CloseIcon from '../../../icons/CloseIcon'
import Spacer from '../../../Spacer'
import Subtotal from '../ProductCheckoutPage/Subtotal'
import { CircularProgress } from '@mui/material'
import CartItem from './CartItem'
import {
  calculateShoppingCartDiscount,
  calculateShoppingCartTotal,
  getShopItemPrice
} from '../../../../utils/shopItemsAndCart'
import { pushEcommerceEvent, buildEcommercePayload } from '../../../../utils/dataLayer'

const cx = (...c) => c.filter(Boolean).join(' ')

function ShoppingCartPanel() {
  // Cart line items now come from the Zustand cart store (Bucket C); the panel-
  // visibility flag moved to the Zustand ui store (Phase C2 part 1). Offers moved
  // to the offers store (Phase D1).
  const shoppingCart = useCartStore(selectCart)
  const showShoppingCartSidePanel = useUiStore((s) => s.showShoppingCartSidePanel)
  const hideCart = useUiStore((s) => s.hideCart)
  const { offers, offersFetched } = useRegionOffers()
  const region = useRegionCountryCode()
  const ensureFullOffers = useOffersStore((s) => s.ensureFullOffers)

  // The shopping cart is restored from localStorage during App.render()
  // (appRendered -> setShoppingCartFromLocalStorage), so on the FIRST client
  // render a returning visitor's cart is already non-empty while the SSR HTML
  // was rendered with an empty cart (localStorage is server-unavailable). The
  // Checkout button's `disabled` is derived directly from cart emptiness, so
  // that difference produced an "Extra attributes from the server: disabled"
  // hydration mismatch (React #418/#423) on every page carrying this global
  // panel. Gate the cart-derived value on a post-mount flag so the first client
  // render matches the SSR HTML (button disabled), then reflect the real cart
  // once hydration has committed — mirroring the deliberately post-mount
  // applyShippingCountryOverlay. The panel is a hidden side-panel, so this is
  // visually neutral: nothing paints differently before or after the flag flips.
  const [mounted, setMounted] = useState(false)
  useEffect(() => { setMounted(true) }, [])

  // This panel is in the global Layout. Load offers ONLY when the cart is
  // actually opened — never on initial render, even for a returning visitor with
  // items. The header badge/count is computed straight from cart state
  // (NavCategory reads shoppingCart, no offers needed); the item details + totals
  // load lazily when the panel opens (brief spinner). This keeps
  // getAllHardwareOffers fully off the page-load critical path on every page.
  // Commerce pages still load offers via their own page triggers / region-SSR.
  useEffect(() => {
    // ensureFullOffers is idempotent; the `!offersFetched` guard preserves today's
    // behavior of NOT re-fetching when the grid offers are already available.
    if (showShoppingCartSidePanel && !offersFetched) {
      ensureFullOffers(region)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showShoppingCartSidePanel])

  useEffect(() => {
    if (!showShoppingCartSidePanel || !offersFetched) return
    const items = Object.keys(shoppingCart)
      .map(id => ({ item: offers.find(i => i.id === id), quantity: shoppingCart[id] }))
      .filter(entry => entry.item)
    if (!items.length) return
    pushEcommerceEvent('view_cart', buildEcommercePayload(items))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showShoppingCartSidePanel, offersFetched])

  if (!offersFetched) {
    return (
      <div className={cx(styles.wrapper, showShoppingCartSidePanel && styles.wrapperShow)}>
        <div className={cx(styles.overlay, showShoppingCartSidePanel && styles.overlayShow)} onClick={() => hideCart()}/>
        <div className={cx(styles.panel, showShoppingCartSidePanel && styles.panelShow)}>
          <div className={styles.loading}>
            <CircularProgress color="inherit"/>
          </div>
        </div>
      </div>
    )
  }

  // Keep items that exist in the catalog — INCLUDING ones with no price, which
  // are not available in the current region (availabilityStatus 'UNAVAILABLE',
  // e.g. a rig left in the cart while browsing /us or /au). They render greyed
  // out with a notice instead of silently disappearing (which made the cart look
  // empty while the badge still showed a count).
  const getItemsInCart = () => {
    return Object.keys(shoppingCart)
      .map(id => offers.find(i => i.id === id))
      .filter(item => !!item)
  }

  const isUnavailableInRegion = (item) => !getShopItemPrice(item)
  const itemsInCart = getItemsInCart()
  const hasUnavailableItems = itemsInCart.some(isUnavailableInRegion)

  return (
    <div className={cx(styles.wrapper, showShoppingCartSidePanel && styles.wrapperShow)}>
      <div className={cx(styles.overlay, showShoppingCartSidePanel && styles.overlayShow)} onClick={() => hideCart()}/>
      <div className={cx(styles.panel, showShoppingCartSidePanel && styles.panelShow)}>
        <div>
          <div className={styles.title}>
            <Text type="heading-5">Cart</Text>
            <div className={styles.closeIconWrapper} onClick={() => hideCart()}><CloseIcon/></div>
          </div>
          <Spacer size={16}/>
          { itemsInCart.map(item => <CartItem key={item.id} shopItem={item}/>) }
          { hasUnavailableItems && (
            <div className={styles.regionNotice}>
              <Text type="small" color="#b3261e">Some items are not available in your region. Remove them to check out.</Text>
            </div>
          )}
        </div>
        <Subtotal
          subtotal={calculateShoppingCartTotal(offers, shoppingCart)}
          discountCents={calculateShoppingCartDiscount(offers, shoppingCart)}
          taxIncluded={offers && offers.length && offers[0].taxIncluded}
          cta={{ title: 'Checkout', disabled: !mounted || Object.keys(shoppingCart).length === 0 || hasUnavailableItems, onClick: () =>
              window.open('/checkout', '_self')
          }}
          ctaSecondary={{ title: 'Continue shopping', light: true, onClick: () => hideCart() }}
          sx={{ pricesVerticallyAligned: true, smallPrice: true }}
        />
      </div>
    </div>
  )
}

export default ShoppingCartPanel
